import { client } from "@/client";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { CustomToastComponent } from "@/components/shared";
import { useMutation } from "@apollo/client/react";
import {
  CreateTenderMutation,
  CreateTenderMutationVariables,
  Tender,
  CreateTenderDocument,
} from "@/graphql/__generated__/graphql";

export default function useCreateTenderMutationHook(): {
  createTenderMutation: any;
  loading: any;
  error: any;
  } {
  const { t } = useTranslation(["toasts"]);

  const [createTenderMutation, { loading, error }] = useMutation<CreateTenderMutation, CreateTenderMutationVariables>(
    CreateTenderDocument, {
      onCompleted(data) {
        toast(CustomToastComponent, {
          toastId: "tender-created-success",
          data: {
            title: t("toasts.tendercreatedsuccessfully"),
            content: t("toasts.customizetender"),
            type: "success",
          },
        });

        updateCache(data.createTender as Tender);
      },
    });

  /**
   * Adds reference of new Tender to cache.
   * @param {Tender} newTender The new tender to be added.
   */
  const updateCache = (newTender: Tender) => {
    const tenderId = newTender.id;
    // Get the cache reference of the new Tender
    const newTenderReference = client.cache.identify({
      __typename: "Tender",
      id: tenderId,
    });

    // Update the cache with the new Tender reference
    // This will add the new Tender to the paginatedTenders query data
    client.cache.modify({
      fields: {
        paginatedTenders(existingPaginatedTenders = {}) {
          const { edges = [], pageInfo } = existingPaginatedTenders;

          // Check if the tender already exists in the cache to avoid duplicates
          const existingTenderIds = edges.map((edge: any) => edge.node.id);
          if (existingTenderIds.includes(tenderId)) {
            return existingPaginatedTenders;
          }

          // Add the new tender reference to the edges field
          const newEdge = {
            __typename: "TenderEdge",
            node: { __ref: newTenderReference },
            cursor: `${tenderId}`,
          };
          return {
            ...existingPaginatedTenders,
            edges: [newEdge, ...edges],
            pageInfo: {
              ...pageInfo,
            },
          };
        },
      },
    });
  };

  return {
    createTenderMutation,
    loading,
    error,
  };
}
